import {
  fetchLearning,
  fetchOperations,
  fetchOverview,
  fetchRevenue,
  fetchAcquisition,
  type Acquisition,
  type Kpi,
  type Learning,
  type MetricStatus,
  type Operations,
  type Overview,
  type Range,
  type Revenue,
} from "../../api/insights";
import {
  Activity,
  AlertTriangle,
  Banknote,
  BarChart3,
  BookOpenCheck,
  CircleCheck,
  CreditCard,
  Download,
  Gauge,
  Megaphone,
  Repeat,
  Server,
  ShieldAlert,
  Smartphone,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Card as BaseCard, Pill, Stat } from "../../components/ui";
import { BarChart, compact, DataTable, LineChart, RankBars } from "./charts";
import { Loading, useSection } from "./section";
import { TutorQualitySection } from "./TutorQuality";

export const RANGE_KEY = "prepai.pulse.range";

const KPI_ICONS: Record<
  string,
  { icon: LucideIcon; tone: "accent" | "positive" | "info" | "warning" }
> = {
  active_students_7d: { icon: Users, tone: "info" },
  questions_answered: { icon: BookOpenCheck, tone: "positive" },
  signups: { icon: UserPlus, tone: "accent" },
  trial_to_paid: { icon: Repeat, tone: "warning" },
  revenue_ngn: { icon: Banknote, tone: "positive" },
  installs: { icon: Smartphone, tone: "info" },
};

const ngn = (n: number) => `₦${compact(n)}`;
const pct = (n: number | null, digits = 0) => (n === null ? "—" : `${(n * 100).toFixed(digits)}%`);

function formatKpi(k: Kpi): string {
  if (k.value === null) return "—";
  if (k.unit === "ngn") return ngn(k.value);
  if (k.unit === "percent") return pct(k.value, 1);
  return k.value.toLocaleString("en-NG");
}

function Change({ k }: { k: Kpi }) {
  if (k.change === null)
    return <span className="text-text-muted">No earlier period to compare</span>;
  const up = k.change >= 0;
  const value =
    k.unit === "percent"
      ? `${up ? "+" : ""}${(k.change * 100).toFixed(1)} pts`
      : `${up ? "+" : ""}${(k.change * 100).toFixed(1)}%`;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 ${up ? "text-positive" : "text-negative"}`}>
      <Icon size={13} strokeWidth={2} aria-hidden="true" />
      {value} <span className="text-text-muted">vs previous</span>
    </span>
  );
}

function StatusChip({ status }: { status: MetricStatus }) {
  return status === "live" ? (
    <Pill tone="positive">Live</Pill>
  ) : (
    <Pill tone="warning">Needs tracking</Pill>
  );
}

function Card({
  title,
  subtitle,
  status,
  icon,
  children,
}: {
  title: string;
  subtitle?: string;
  status?: MetricStatus;
  icon?: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <BaseCard
      title={title}
      {...(subtitle ? { subtitle } : {})}
      {...(icon ? { icon } : {})}
      aside={status ? <StatusChip status={status} /> : undefined}
    >
      {children}
    </BaseCard>
  );
}

function Updated({ at }: { at: string | null }) {
  return (
    <p className="text-[11px] text-text-muted">
      {at
        ? `Updated ${new Date(at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`
        : "Waiting for the first hourly rollup."}
    </p>
  );
}

export type Section = "overview" | "learning" | "revenue" | "acquisition" | "tutor" | "operations";

/** One leadership section, for the dashboard's tabs. */
export function InsightsSection({ section, range }: { section: Section; range: Range }) {
  if (section === "overview") return <OverviewSection range={range} />;
  if (section === "learning") return <LearningSection range={range} />;
  if (section === "revenue") return <RevenueSection range={range} />;
  if (section === "acquisition") return <AcquisitionSection range={range} />;
  if (section === "tutor") return <TutorQualitySection range={range} />;
  return <OperationsSection />;
}

function OverviewSection({ range }: { range: Range }) {
  const { data, error } = useSection<Overview>(() => fetchOverview(range), [range]);
  if (!data) return <Loading error={error} />;

  const signups = data.funnel[0]?.value ?? 0;
  const steps = data.funnel.map((s, i) => {
    const prev = data.funnel[i - 1]?.value ?? null;
    return {
      ...s,
      share: signups && s.value !== null ? s.value / signups : null,
      lost: prev && s.value !== null ? 1 - s.value / prev : null,
    };
  });
  const worst = steps
    .slice(1)
    .reduce<(typeof steps)[number] | null>(
      (w, s) => ((s.lost ?? 0) > (w?.lost ?? -1) ? s : w),
      null,
    );
  const worstFrom = worst ? steps[steps.indexOf(worst) - 1] : null;

  return (
    <div className="flex flex-col gap-4">
      <Updated at={data.updated_at} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {data.kpis.map((k) => {
          const look = KPI_ICONS[k.key] ?? { icon: Gauge, tone: "info" as const };
          return (
            <Stat
              key={k.key}
              label={k.label}
              value={formatKpi(k)}
              icon={look.icon}
              tone={look.tone}
              hint={<Change k={k} />}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <Card
          title="Signup to paid"
          subtitle="Students who reached each step in the period"
          icon={BarChart3}
        >
          <ul className="flex flex-col gap-3">
            {steps.map((s) => (
              <li key={s.key}>
                <div className="flex items-center justify-between text-[13px]">
                  <span className="flex items-center gap-2">
                    {s.label}
                    {s.status === "needs_tracking" ? <StatusChip status="needs_tracking" /> : null}
                  </span>
                  <span className="tabular-nums text-text-muted">
                    {(s.value ?? 0).toLocaleString("en-NG")} · {pct(s.share)}
                  </span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded bg-surface-high">
                  <div
                    className="h-full rounded bg-accent"
                    style={{ width: `${(s.share ?? 0) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Where students drop" subtitle="Biggest step-to-step loss" icon={TrendingDown}>
          {worst && worstFrom && worst.lost !== null ? (
            <>
              <p className="font-[family-name:var(--font-display)] text-[36px] font-semibold leading-none tabular-nums text-negative">
                {pct(worst.lost)}
              </p>
              <p className="text-[13px] text-text-muted">
                lost between <strong className="text-text">{worstFrom.label.toLowerCase()}</strong>{" "}
                and <strong className="text-text">{worst.label.toLowerCase()}</strong>.
              </p>
            </>
          ) : (
            <p className="text-[13px] text-text-muted">
              Not enough signups in this period to compare steps.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}

function LearningSection({ range }: { range: Range }) {
  const { data, error } = useSection<Learning>(() => fetchLearning(range), [range]);
  if (!data) return <Loading error={error} />;
  const e = data.engagement;
  const maxWeeks = Math.max(0, ...data.cohorts.map((c) => c.retention.length));

  return (
    <div className="flex flex-col gap-4">
      <Updated at={data.updated_at} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <Card
          title="Daily active students"
          subtitle="Students with at least one practice session that day"
          status="live"
          icon={Users}
        >
          <LineChart points={data.daily_active} label="Active students" />
        </Card>
        <Card
          title="Questions by subject"
          subtitle="Answers submitted in the period"
          status="live"
          icon={BookOpenCheck}
        >
          <RankBars items={data.by_subject} />
        </Card>
      </div>
      <Card
        title="Weekly retention by signup cohort"
        subtitle="Share of each week's signups who practised in week N"
        status="live"
        icon={Repeat}
      >
        {data.cohorts.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-[12px]">
              <thead>
                <tr className="text-text-muted">
                  <th className="px-2 py-1 text-left font-medium">Cohort</th>
                  {Array.from({ length: maxWeeks }, (_, i) => (
                    <th key={i} className="px-2 py-1 font-medium">
                      Wk {i}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.cohorts.map((c) => (
                  <tr key={c.cohort}>
                    <td className="whitespace-nowrap px-2 py-1">
                      {new Date(`${c.cohort}T00:00:00Z`).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        timeZone: "UTC",
                      })}
                    </td>
                    {Array.from({ length: maxWeeks }, (_, i) => {
                      const v = c.retention[i];
                      return (
                        <td key={i} className="px-1 py-1 text-center">
                          {v === undefined ? null : (
                            <span
                              className="block rounded px-1 py-1 tabular-nums"
                              style={{
                                background:
                                  v === null
                                    ? "transparent"
                                    : `color-mix(in srgb, var(--color-accent) ${Math.round(v * 70)}%, transparent)`,
                              }}
                            >
                              {v === null ? "—" : pct(v)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-[13px] text-text-muted">
            Cohorts appear after the first Sunday rollup.
          </p>
        )}
      </Card>
      <Card title="Engagement quality" subtitle="Period average" icon={Activity}>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            [
              "Questions per active student / day",
              e.questions_per_active_day === null ? "—" : e.questions_per_active_day.toFixed(1),
            ],
            ["Answers that opened the AI tutor", pct(e.tutor_rate, 1)],
            ["Practice sessions completed", pct(e.session_completion)],
            ["Parent consent approved", pct(e.consent_approval)],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="font-[family-name:var(--font-display)] text-[24px] font-semibold tabular-nums">
                {value}
              </p>
              <p className="text-[12px] text-text-muted">{label}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function RevenueSection({ range }: { range: Range }) {
  const { data, error } = useSection<Revenue>(() => fetchRevenue(range), [range]);
  if (!data) return <Loading error={error} />;
  const outcomeTotal = data.outcomes.reduce((a, o) => a + (o.value ?? 0), 0);
  const methodTotal = data.methods.reduce((a, o) => a + (o.value ?? 0), 0);
  const outcomeLabel: Record<string, string> = {
    successful: "Paid",
    pending: "Pending",
    failed: "Failed",
    refunded: "Refunded",
  };

  return (
    <div className="flex flex-col gap-4">
      <Updated at={data.updated_at} />
      <p className="text-[13px] text-text-muted">
        {ngn(data.total_revenue)} from {data.total_payments.toLocaleString("en-NG")} payments ·
        one-off access · Paystack
      </p>
      <Card
        title="Daily revenue"
        subtitle="Successful Paystack payments, ₦"
        status="live"
        icon={Banknote}
      >
        <BarChart
          points={data.daily.map((d) => ({ date: d.date, value: d.revenue }))}
          label="Revenue"
          format={ngn}
        />
      </Card>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card
          title="Checkout outcomes"
          subtitle="Checkouts started in the period"
          status="live"
          icon={CircleCheck}
        >
          <RankBars
            items={data.outcomes.map((o) => ({ ...o, key: outcomeLabel[o.key] ?? o.key }))}
            total={outcomeTotal}
          />
        </Card>
        <Card
          title="Payment method"
          subtitle="Share of successful payments"
          status="live"
          icon={CreditCard}
        >
          <RankBars items={data.methods} total={methodTotal} />
        </Card>
      </div>
    </div>
  );
}

const CHANNEL_LABELS: Record<string, string> = {
  meta: "Meta (Facebook, Instagram)",
  tiktok: "TikTok",
  google: "Google",
  whatsapp: "WhatsApp",
  other_paid: "Other paid",
  referral: "Referral",
  organic: "Search (organic)",
  direct: "Direct",
  unknown: "Not tracked",
};
const channelLabel = (key: string) => CHANNEL_LABELS[key] ?? key;
const share = (n: number | null) => (n === null ? "—" : `${Math.round(n * 100)}%`);
const count = (n: number | null) => (n === null ? "Under 5" : n.toLocaleString("en-NG"));

function AcquisitionSection({ range }: { range: Range }) {
  const { data, error } = useSection<Acquisition>(() => fetchAcquisition(range), [range]);
  if (!data) return <Loading error={error} />;
  const tracked = data.channels.filter((c) => c.key !== "unknown");

  return (
    <div className="flex flex-col gap-4">
      <Updated at={data.updated_at} />
      <p className="text-[13px] text-text-muted">
        {data.total_signups.toLocaleString("en-NG")} signups · {share(data.tracked_share)} carried a
        channel · first-touch: each student counts for the ad or link that first brought them
      </p>
      {data.tracked_share !== null && data.tracked_share < 0.5 ? (
        <div className="flex gap-3 rounded-2xl border border-warning/40 bg-warning/10 p-4 text-[13px]">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
          <p className="text-text-muted">
            Most signups here have no channel. That is expected for students who joined before
            tracking started; for new ones, tag every ad and shared link with{" "}
            <code>utm_source</code> and <code>utm_campaign</code>.
          </p>
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card
          title="Signups by channel"
          subtitle="Students who signed up in the period"
          status="live"
          icon={Megaphone}
        >
          <RankBars
            items={data.channels.map((c) => ({ key: channelLabel(c.key), value: c.signups }))}
          />
        </Card>
        <Card
          title="Paid students by channel"
          subtitle="Of those signups, how many have paid since"
          status="live"
          icon={Banknote}
        >
          <DataTable
            headers={["Channel", "Signups", "Paid", "Conversion"]}
            rows={tracked.map((c) => [
              channelLabel(c.key),
              count(c.signups),
              count(c.paid),
              share(c.conversion),
            ])}
          />
          {tracked.length === 0 ? (
            <p className="mt-2 text-[13px] text-text-muted">No tracked signups in this period.</p>
          ) : null}
        </Card>
      </div>
      <Card
        title="Campaigns"
        subtitle="From utm_campaign; top 25 by signups"
        status="live"
        icon={TrendingUp}
      >
        <DataTable
          headers={["Campaign", "Channel", "Signups", "Paid", "Conversion"]}
          rows={data.campaigns.map((c) => [
            c.campaign,
            channelLabel(c.channel),
            count(c.signups),
            count(c.paid),
            share(c.conversion),
          ])}
        />
        {data.campaigns.length === 0 ? (
          <p className="mt-2 text-[13px] text-text-muted">
            No campaign-tagged signups yet. Add <code>utm_campaign</code> to ad links to see them
            here.
          </p>
        ) : null}
      </Card>
      <Card
        title="Ad spend and cost per paid student"
        subtitle="Meta, TikTok and Google Ads spend"
        status="needs_tracking"
        icon={Download}
      >
        <p className="text-[13px] text-text-muted">
          Needs each ad platform's reporting API connected with an access token, so spend can sit
          next to the paid students above. Signups and paid students already come from PrepAI's own
          records.
        </p>
      </Card>
    </div>
  );
}

const TONE: Record<string, "positive" | "warning" | "negative"> = {
  healthy: "positive",
  watch: "warning",
  investigate: "negative",
};
const TONE_TEXT: Record<string, string> = {
  healthy: "Healthy",
  watch: "Watch",
  investigate: "Investigate",
};
const TONE_BAR: Record<string, string> = {
  healthy: "bg-positive",
  watch: "bg-warning",
  investigate: "bg-negative",
};

function OperationsSection() {
  const { data, error } = useSection<Operations>(() => fetchOperations(), []);
  if (!data) return <Loading error={error} />;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-[11px] text-text-muted">
        Checked {new Date(data.checked_at).toLocaleTimeString("en-GB", { timeStyle: "short" })}.
        Alerts are checked every 5 minutes and emailed.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.tiles.map((t) => (
          <div
            key={t.key}
            className="relative overflow-hidden rounded-2xl border border-border bg-surface p-4"
          >
            <span
              aria-hidden="true"
              className={`absolute inset-y-0 left-0 w-1 ${t.status === "live" ? TONE_BAR[t.state] : "bg-surface-high"}`}
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-[12px] font-medium text-text-muted">{t.label}</p>
              {t.status === "live" ? (
                <Pill tone={TONE[t.state]!}>{TONE_TEXT[t.state]}</Pill>
              ) : (
                <StatusChip status="needs_tracking" />
              )}
            </div>
            <p className="mt-2 font-[family-name:var(--font-display)] text-[26px] font-semibold tabular-nums">
              {t.value === null
                ? "—"
                : t.unit === "percent"
                  ? pct(t.value, t.key === "api_uptime" ? 2 : 1)
                  : t.unit === "seconds"
                    ? `${t.value.toFixed(1)} s`
                    : t.value.toLocaleString("en-NG")}
            </p>
            <p className="mt-1 text-[11px] leading-snug text-text-muted">{t.note}</p>
            {t.link ? (
              <a
                href={t.link}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-[11px] font-semibold text-accent-light"
              >
                {t.linkLabel ?? "View details"}
              </a>
            ) : null}
          </div>
        ))}
      </div>
      <Card title="Recent incidents" subtitle="From the alert log" icon={ShieldAlert}>
        {data.incidents.length ? (
          <ul className="flex flex-col gap-2 text-[13px]">
            {data.incidents.map((i) => (
              <li
                key={`${i.key}${i.opened_at}`}
                className="flex flex-col gap-0.5 border-b border-border pb-2 last:border-0"
              >
                <span className="flex items-center justify-between gap-2">
                  <strong>{i.title}</strong>
                  <Pill tone={i.resolved_at ? "positive" : "negative"}>
                    {i.resolved_at ? "Resolved" : "Open"}
                  </Pill>
                </span>
                <span className="text-text-muted">{i.detail}</span>
                <span className="text-[11px] text-text-muted">
                  {new Date(i.opened_at).toLocaleString("en-GB")}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="flex items-center gap-2 text-[13px] text-text-muted">
            <Server size={15} aria-hidden="true" /> No incidents. Everything is running.
          </p>
        )}
      </Card>
    </div>
  );
}
